import { X, Link2, CheckCircle2, UserPlus, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from '../../lib/utils';
import type { EnrichedIncident } from '../../data/types';

interface BulkActionToolbarProps {
  selectedCount: number;
  selectedIncidents: EnrichedIncident[];
  onClearSelection: () => void;
  onBulkClose: () => void;
  onBulkLink: () => void;
  onBulkAssign: () => void;
  className?: string;
}

export function BulkActionToolbar({
  selectedCount,
  selectedIncidents,
  onClearSelection,
  onBulkClose,
  onBulkLink,
  onBulkAssign,
  className,
}: BulkActionToolbarProps) {
  if (selectedCount === 0) return null;

  // Calculate severity breakdown
  const severityBreakdown = selectedIncidents.reduce(
    (acc, inc) => {
      acc[inc.severity] = (acc[inc.severity] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const hasCritical = (severityBreakdown['critical'] || 0) > 0;
  const hasHigh = (severityBreakdown['high'] || 0) > 0;

  // Get unique tenants
  const tenantCount = new Set(selectedIncidents.map((i) => i.tenantId)).size;

  return (
    <div
      className={cn(
        'fixed bottom-6 left-1/2 -translate-x-1/2 z-40',
        'bg-brand-navy dark:bg-bg-secondary rounded-xl shadow-2xl border border-border-default',
        'px-4 py-3 flex items-center gap-4',
        'animate-slide-up',
        className
      )}
    >
      {/* Selection info */}
      <div className="flex items-center gap-3 pr-4 border-r border-border-default">
        <span className="text-white font-medium">
          {selectedCount} incident{selectedCount !== 1 ? 's' : ''} selected
        </span>
        {tenantCount > 1 && (
          <span className="text-xs text-text-muted bg-brand-navy px-2 py-0.5 rounded">
            {tenantCount} tenants
          </span>
        )}
        {(hasCritical || hasHigh) && (
          <span
            className={cn(
              'text-xs px-2 py-0.5 rounded flex items-center gap-1',
              hasCritical
                ? 'bg-red-900/50 text-red-300'
                : 'bg-orange-900/50 text-orange-300'
            )}
          >
            <AlertTriangle className="w-3 h-3" />
            {hasCritical
              ? `${severityBreakdown['critical']} critical`
              : `${severityBreakdown['high']} high`}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBulkLink}
          className="text-slate-300 hover:text-white hover:bg-slate-700"
        >
          <Link2 className="w-4 h-4 mr-1.5" />
          Link
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={onBulkAssign}
          className="text-slate-300 hover:text-white hover:bg-slate-700"
        >
          <UserPlus className="w-4 h-4 mr-1.5" />
          Assign
        </Button>

        <Button
          variant="primary"
          size="sm"
          onClick={onBulkClose}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <CheckCircle2 className="w-4 h-4 mr-1.5" />
          Close All
        </Button>
      </div>

      {/* Clear selection */}
      <button
        type="button"
        onClick={onClearSelection}
        className="ml-2 p-1.5 rounded-full hover:bg-slate-700 text-text-muted hover:text-white transition-colors"
        aria-label="Clear selection"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
